import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import {
    McButton,
    McIcon,
    McSelect,
    McInput,
    McLoadingIndicator,
    McNotification,
    McTable,
    McTabBar,
    McTab,
} from '@maersk-global/mds-react-wrapper';
import { McTooltip as McTooltipMDS } from '@maersk-global/mds-react-wrapper/components-core/mc-tooltip';
import { TerminalSelect } from '../../Common/TerminalSelect';
import { DatabaseSelect } from '../../Common/DatabaseSelect';
import { ConnectionSelect } from '../../Common/ConnectionSelect';
import { SqlEditor } from '../../Common/SqlEditor';
import type { SqlEditorHandle } from '../../Common/SqlEditor';
import type { DbConnection } from '../../../types/registry';
import { DownloadResultsButton } from '../../Common/DownloadResultsButton';
import { ResultFilterBar } from '../../Common/ResultFilterBar';
import { ValidationResponse } from '../../../types';
import { DatabaseType } from '../../../utils/terminalConfig';
import { ExplainPlanCard } from '../../Common/ExplainPlanCard';
import ParameterRow from '../../Common/ParameterRow';
import { SchemaExplorer } from '../../SchemaExplorer';
import { ChartVisualization } from '../../Common/ChartVisualization';

interface TestParameter {
    name: string;
    type: 'STRING' | 'NUMBER' | 'BOOLEAN' | 'DATE' | 'TIMESTAMP' | 'STRING_LIST';
    value: string;
    description?: string;
}

interface SqlStepProps {
    sqlContent: string;
    onSqlChange: (value: string) => void;
    testTerminal: string;
    onTestTerminalChange: (value: string) => void;
    testDatabaseType: DatabaseType;
    onTestDatabaseTypeChange: (value: DatabaseType) => void;
    connectionId?: string;
    onConnectionChange?: (connectionId: string, connection?: DbConnection) => void;
    onExecute?: () => void;
    isExecuting?: boolean;
    validationResult: ValidationResponse | null;
    testResults: any;
    testParameters: TestParameter[];
    onTestParameterChange: (index: number, field: string, value: any) => void;
    onBack: () => void;
    onNext?: () => void;
    onSave: () => void;
    isSaving: boolean;
    isExplainingPlan?: boolean;
    aiExplanation?: string | null;
    isGeneratingAiExplanation?: boolean;
    onAiExplain?: () => void;
    onExplainPlan?: () => void;
    onCloseExplainPlan?: () => void;
    onAiAssist?: () => void;
    isAiGenerating?: boolean;
    onAutoFix?: () => void;
    isAutoFixing?: boolean;
    autoFixModel?: string;
    onAutoFixModelChange?: (model: string) => void;
    autoFixModelOptions?: { label: string; value: string }[];
    unfixableReason?: string | null;
    aiCorrectionDiff?: { original: string; corrected: string } | null;
    onUndoCorrection?: () => void;
    onViewChanges?: () => void;
    aiModelOptions?: { label: string; value: string }[];
    selectedModel?: string;
    onModelChange?: (model: string) => void;
    /** Base SQL for diff highlighting when a terminal override is active */
    overrideBaseSql?: string;
    /** Whether a terminal override is currently loaded */
    isOverride?: boolean;
    /** Save current SQL as a terminal-specific override */
    onSaveOverride?: () => void;
    isSavingOverride?: boolean;
    /** Remove the active terminal override */
    onDeleteOverride?: () => void;
    isDeletingOverride?: boolean;
    /** Ref to the SQL editor for programmatic insertion (e.g. from schema panel) */
    sqlEditorRef?: React.Ref<SqlEditorHandle>;
    /** Toggle the schema side panel open/closed */
    onToggleSchemaPanel?: () => void;
    /** Whether the schema panel is currently open (drives active-state styling) */
    schemaPanelOpen?: boolean;
    /** Open the fullscreen schema modal */
    onOpenSchemaFullscreen?: () => void;
    /** Open the panel and jump directly to a specific schema tab */
    onOpenSchemaPanelTab?: (tab: 'schema' | 'field-search' | 'field-catalog') => void;
    /** Currently active schema panel tab (for active-state styling of the tab shortcuts) */
    schemaPanelTab?: 'schema' | 'field-search' | 'field-catalog';
    /** Called when a snippet is inserted from the schema panel */
    onInsertSnippet?: (text: string) => void;
    /** Inline warning shown near the schema toolbar */
    schemaExplorerWarning?: string | null;
}

type ResultTab = 'results' | 'explain' | 'parameters' | 'visualization';

/** Thin vertical separator between toolbar button groups */
const BtnSep: React.FC = () => (
    <div style={{ width: 1, height: 20, backgroundColor: '#e5e7eb', margin: '0 4px', flexShrink: 0 }} />
);

export const SqlStep: React.FC<SqlStepProps> = ({
    sqlContent,
    onSqlChange,
    testTerminal,
    onTestTerminalChange,
    testDatabaseType,
    onTestDatabaseTypeChange,
    connectionId,
    onConnectionChange,
    onExecute,
    isExecuting = false,
    validationResult,
    testResults,
    testParameters,
    onTestParameterChange,
    onBack,
    onNext,
    onSave,
    isSaving,
    isExplainingPlan = false,
    aiExplanation = null,
    isGeneratingAiExplanation = false,
    onAiExplain,
    onExplainPlan,
    onCloseExplainPlan,
    onAiAssist,
    isAiGenerating = false,
    onAutoFix,
    isAutoFixing = false,
    autoFixModel,
    onAutoFixModelChange,
    autoFixModelOptions = [],
    unfixableReason = null,
    aiCorrectionDiff = null,
    onUndoCorrection,
    onViewChanges,
    aiModelOptions = [],
    selectedModel,
    onModelChange,
    overrideBaseSql,
    isOverride = false,
    onSaveOverride,
    isSavingOverride = false,
    onDeleteOverride,
    isDeletingOverride = false,
    sqlEditorRef,
    onToggleSchemaPanel,
    schemaPanelOpen = false,
    onOpenSchemaFullscreen,
    onOpenSchemaPanelTab,
    schemaPanelTab,
    onInsertSnippet,
    schemaExplorerWarning,
}) => {
    // Stable refs for callbacks
    const latestOnExecute = useRef(onExecute);
    const latestOnBack = useRef(onBack);
    const latestOnNext = useRef(onNext);
    const latestOnSave = useRef(onSave);
    const latestOnTestParameterChange = useRef(onTestParameterChange);
    useEffect(() => { latestOnExecute.current = onExecute; }, [onExecute]);
    useEffect(() => { latestOnBack.current = onBack; }, [onBack]);
    useEffect(() => { latestOnNext.current = onNext; }, [onNext]);
    useEffect(() => { latestOnSave.current = onSave; }, [onSave]);
    useEffect(() => { latestOnTestParameterChange.current = onTestParameterChange; }, [onTestParameterChange]);

    // Layout state
    const [editorRatio, setEditorRatio] = useState(0.7);
    const [externalDdlEnabled, setExternalDdlEnabled] = useState(false);
    const [externalConnectionServerType, setExternalConnectionServerType] = useState<'POSTGRESQL' | 'ORACLE' | undefined>(undefined);
    const editorColumnRef = useRef<HTMLDivElement>(null);
    const schemaOverlayHostRef = useRef<HTMLDivElement>(null);
    const [schemaBackdropTop, setSchemaBackdropTop] = useState(0);

    // Close schema overlay when clicking outside (dropdown behavior)
    useEffect(() => {
        if (!schemaPanelOpen || !onToggleSchemaPanel) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (schemaOverlayHostRef.current && !schemaOverlayHostRef.current.contains(e.target as Node)) {
                onToggleSchemaPanel();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [schemaPanelOpen, onToggleSchemaPanel]);

    useEffect(() => {
        if (!schemaPanelOpen || !onToggleSchemaPanel) return;
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onToggleSchemaPanel();
        };
        window.addEventListener('keydown', onEsc);
        return () => window.removeEventListener('keydown', onEsc);
    }, [schemaPanelOpen, onToggleSchemaPanel]);

    useEffect(() => {
        if (!schemaPanelOpen) return;
        const updateBackdropTop = () => {
            const hostBottom = schemaOverlayHostRef.current?.getBoundingClientRect().bottom ?? 0;
            setSchemaBackdropTop(hostBottom);
        };
        updateBackdropTop();
        window.addEventListener('resize', updateBackdropTop);
        window.addEventListener('scroll', updateBackdropTop, true);
        return () => {
            window.removeEventListener('resize', updateBackdropTop);
            window.removeEventListener('scroll', updateBackdropTop, true);
        };
    }, [schemaPanelOpen]);

    // Result panel state
    const [activeResultTab, setActiveResultTab] = useState<ResultTab>('results');
    const [resultColumnFilters, setResultColumnFilters] = useState<Record<string, string>>({});

    // Auto-switch to Parameters tab when params appear/disappear
    useEffect(() => {
        if (testParameters.length > 0) {
            setActiveResultTab('parameters');
        } else {
            setActiveResultTab(prev => prev === 'parameters' ? 'results' : prev);
        }
    }, [testParameters.length]);

    // Switch to Explain tab when explain plan runs
    useEffect(() => {
        if (isExplainingPlan || validationResult?.planLines?.length) {
            setActiveResultTab('explain');
        }
    }, [isExplainingPlan, validationResult?.planLines?.length]);

    // Reset column filters when result set changes
    useEffect(() => { setResultColumnFilters({}); }, [testResults?.data]);

    // Derived values
    const _execNoTarget = testDatabaseType === 'EXTERNAL' ? !connectionId : !testTerminal;
    const _canExecSql = !isExecuting && !!sqlContent.trim() && !_execNoTarget;

    const resultColumns = useMemo(() => {
        if (!testResults?.data || testResults.data.length === 0) return testResults?.columns || [];
        return testResults.columns || Object.keys(testResults.data[0]).map((key: string) => ({
            id: key, label: key, accessor: (row: any) => row[key],
        }));
    }, [testResults]);

    const filteredTestResults = useMemo(() => {
        const rows = testResults?.data || [];
        const active = Object.entries(resultColumnFilters).filter(([, v]) => v && (v as string).trim());
        if (!active.length) return rows;
        return rows.filter((row: any) =>
            active.every(([col, val]) =>
                String(row?.[col] ?? '').toLowerCase().includes(String(val).toLowerCase())
            )
        );
    }, [testResults, resultColumnFilters]);

    // Vertical drag resizer handler
    const handleResizerMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        const startY = e.clientY;
        const initialRatio = editorRatio;
        const onMove = (me: MouseEvent) => {
            const col = editorColumnRef.current;
            if (!col) return;
            const h = col.getBoundingClientRect().height;
            if (!h) return;
            const delta = (me.clientY - startY) / h;
            setEditorRatio(Math.max(0.15, Math.min(0.85, initialRatio + delta)));
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [editorRatio]);

    // AutoFix model picker (reused in validation + exec-error panels)
    const AutoFixModelPicker = useCallback(() =>
        autoFixModelOptions.length > 0 ? (
            <div style={{ display: 'flex', gap: 0, borderRadius: 999, overflow: 'hidden', border: '1px solid #d6d6d6' }}>
                {autoFixModelOptions.map(opt => (
                    <button key={opt.value} onClick={() => onAutoFixModelChange?.(opt.value)}
                        style={{
                            padding: '3px 10px', fontSize: '0.78rem',
                            fontWeight: autoFixModel === opt.value ? 600 : 400,
                            cursor: 'pointer', border: 'none',
                            background: autoFixModel === opt.value ? '#0073ab' : 'transparent',
                            color: autoFixModel === opt.value ? '#fff' : '#141414',
                            transition: 'background 0.15s, color 0.15s', whiteSpace: 'nowrap',
                        }}
                    >{opt.label}</button>
                ))}
            </div>
        ) : null
    , [autoFixModelOptions, autoFixModel, onAutoFixModelChange]);

    // Tab button helper
    const tabBtn = (tab: ResultTab, label: string, hasDot: boolean) => (
        <button
            key={tab}
            onClick={() => setActiveResultTab(tab)}
            style={{
                padding: '0 14px', height: 36, border: 'none', cursor: 'pointer',
                background: 'transparent', fontSize: '0.8rem',
                fontWeight: activeResultTab === tab ? 700 : 400,
                color: activeResultTab === tab ? '#0073ab' : '#6b7280',
                borderBottom: activeResultTab === tab ? '2px solid #0073ab' : '2px solid transparent',
                display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
            }}
        >
            {label}
            {hasDot && tab !== activeResultTab && (
                <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#f59e0b', display: 'inline-block' }} />
            )}
        </button>
    );

    return (
        <div style={{
            display: 'flex', flexDirection: 'column',
            height: 'calc(100vh - 240px)', minHeight: 500,
            backgroundColor: '#fff', border: '1px solid #e5e7eb',
            borderRadius: 8, overflow: 'hidden',
        }}>
            {/* ═══ TOP BAR + OVERLAY HOST ═══ */}
            <div ref={schemaOverlayHostRef} style={{ position: 'relative', flexShrink: 0, zIndex: 5 }}>
            <div style={{
                display: 'flex', alignItems: 'center', minHeight: 56, flexShrink: 0,
                borderBottom: '1px solid #e5e7eb', padding: '6px 8px', gap: 8,
                backgroundColor: '#f9fafb', flexWrap: 'wrap'
            }}>
                {/* Left: DB type + terminal/connection selects */}
                <div style={{ width: 130, flexShrink: 0 }}>
                    <DatabaseSelect label="" value={testDatabaseType} onChange={onTestDatabaseTypeChange} />
                </div>
                {testDatabaseType === 'EXTERNAL' ? (
                    <div style={{ width: 260, flexShrink: 0 }}>
                        <ConnectionSelect
                            value={connectionId || ''}
                            onChange={(id, conn) => {
                                setExternalDdlEnabled(conn?.allowedOps === 'DDL');
                                setExternalConnectionServerType(conn?.serverType);
                                onConnectionChange?.(id, conn);
                            }}
                            onResolvedConnection={(conn) => {
                                setExternalDdlEnabled(conn?.allowedOps === 'DDL');
                                setExternalConnectionServerType(conn?.serverType);
                            }}
                        />
                    </div>
                ) : (
                    <div style={{ width: 220, flexShrink: 0 }}>
                        <TerminalSelect label="" value={testTerminal} onChange={onTestTerminalChange} />
                    </div>
                )}
                {/* Override chip */}
                {isOverride && testTerminal && (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        backgroundColor: '#f59e0b', color: '#fff',
                        fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                        padding: '2px 8px', borderRadius: 999, flexShrink: 0,
                    }}>⚡ {testTerminal}</span>
                )}

                {/* Center: schema tab shortcuts */}
                {onOpenSchemaPanelTab && (
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <McTabBar
                            currentindex={schemaPanelOpen ? (schemaPanelTab === 'schema' ? 0 : schemaPanelTab === 'field-search' ? 1 : 2) : undefined}
                            tabchange={(e: any) => {
                                const idx = e.detail;
                                onOpenSchemaPanelTab(idx === 0 ? 'schema' : idx === 1 ? 'field-search' : 'field-catalog');
                            }}
                        >
                            <McTab slot="tab" label="" icon="table" title="Schema Browser" />
                            <McTab slot="tab" label="" icon="magnifying-glass" title="Field Intelligence" />
                            <McTab slot="tab" label="" icon="tag" title="Field Catalog" />
                        </McTabBar>
                    </div>
                )}

                {/* Right: action button groups */}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                    {/* Execute */}
                    {onExecute && (
                        <McTooltipMDS onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                            {/* @ts-ignore */}
                            <McButton slot="trigger" fit="small"
                                variant={_canExecSql ? 'outlined' : 'plain'} style={{ minWidth: 36, padding: '0 8px' }}
                                appearance={_canExecSql ? 'primary' : undefined}
                                disabled={!_canExecSql && !isExecuting}
                                onClick={() => latestOnExecute.current?.()}
                            >
                                {isExecuting
                                    ? <McLoadingIndicator fit="small" />
                                    : <McIcon icon="paper-plane" />}
                            </McButton>
                            <span>{isExecuting ? 'Running…' : 'Execute query'}</span>
                        </McTooltipMDS>
                    )}
                    {/* Explain Plan */}
                    {onExplainPlan && (
                        <McTooltipMDS onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                            {/* @ts-ignore */}
                            <McButton slot="trigger" fit="small" variant="plain" style={{ minWidth: 36, padding: "0 8px" }}
                                disabled={!sqlContent.trim() || isExecuting || isExplainingPlan || _execNoTarget}
                                onClick={() => onExplainPlan?.()}
                            >
                                {isExplainingPlan
                                    ? <McLoadingIndicator fit="small" />
                                    : <McIcon icon="lightning" />}
                            </McButton>
                            <span>Explain query plan</span>
                        </McTooltipMDS>
                    )}
                    {/* AI Assistant */}
                    {onAiAssist && (
                        <McTooltipMDS onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                            {/* @ts-ignore */}
                            <McButton slot="trigger" fit="small" variant="plain" style={{ minWidth: 36, padding: "0 8px" }}
                                disabled={isAiGenerating}
                                onClick={onAiAssist}
                            >
                                <McIcon icon="chatbot" />
                            </McButton>
                            <span>Open AI assistant</span>
                        </McTooltipMDS>
                    )}

                    {onToggleSchemaPanel && (
                        <>
                            <BtnSep />
                            <McTooltipMDS onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                                {/* @ts-ignore */}
                                <McButton slot="trigger" fit="small"
                                    variant={schemaPanelOpen ? 'filled' : 'plain'}
                                    appearance={schemaPanelOpen ? 'primary' : undefined}
                                    style={{ minWidth: 36, padding: '0 8px' }}
                                    aria-pressed={schemaPanelOpen}
                                    onClick={onToggleSchemaPanel}
                                >
                                    <McIcon icon="database" />
                                </McButton>
                                <span>{schemaPanelOpen ? 'Hide schema panel' : 'Show schema panel'}</span>
                            </McTooltipMDS>
                            {onOpenSchemaFullscreen && (
                                <McTooltipMDS onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                                    {/* @ts-ignore */}
                                    <McButton slot="trigger" fit="small" variant="plain" style={{ minWidth: 36, padding: '0 8px' }}
                                        onClick={onOpenSchemaFullscreen}
                                    >
                                        <McIcon icon="table" />
                                    </McButton>
                                    <span>Schema explorer (fullscreen)</span>
                                </McTooltipMDS>
                            )}
                        </>
                    )}

                    <BtnSep />

                    {/* Save base query — hidden when a terminal override is active */}
                    {!isOverride && (
                    <McTooltipMDS onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                        {/* @ts-ignore */}
                        <McButton slot="trigger" fit="small" variant="plain" style={{ minWidth: 36, padding: "0 8px" }}
                            disabled={isSaving || isSavingOverride || isDeletingOverride || !sqlContent.trim()}
                            onClick={() => latestOnSave.current()}
                        >
                            {isSaving
                                ? <McLoadingIndicator fit="small" />
                                : <McIcon icon="floppy-disk" />}
                        </McButton>
                        <span>{isSaving ? 'Updating…' : 'Update base query'}</span>
                    </McTooltipMDS>
                    )}
                    {/* Save override */}
                    {testTerminal && onSaveOverride && (
                        <McTooltipMDS onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                            {/* @ts-ignore */}
                            <McButton slot="trigger" icon="star" fit="small" variant="plain" style={{ minWidth: 36, padding: "0 8px" }}
                                disabled={isSavingOverride || isSaving || isDeletingOverride || !sqlContent.trim()}
                                onClick={onSaveOverride}
                            >
                                {isSavingOverride
                                    ? <McLoadingIndicator fit="small" />
                                    : <span style={{ fontSize: 14, lineHeight: 1 }}></span>}
                            </McButton>
                            <span>{isSavingOverride ? 'Saving…' : (isOverride ? `Update ⚡ ${testTerminal} override` : `Save as ⚡ ${testTerminal} override`)}</span>
                        </McTooltipMDS>
                    )}
                    {/* Delete override */}
                    {isOverride && onDeleteOverride && (
                        <McTooltipMDS onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                            {/* @ts-ignore */}
                            <McButton slot="trigger" fit="small" variant="plain" style={{ minWidth: 36, padding: "0 8px" }} appearance="error"
                                disabled={isDeletingOverride || isSaving || isSavingOverride}
                                onClick={onDeleteOverride}
                            >
                                {isDeletingOverride
                                    ? <McLoadingIndicator fit="small" />
                                    : <McIcon icon="trash" />}
                            </McButton>
                            <span>{isDeletingOverride ? 'Removing…' : `Deactivate ⚡ ${testTerminal} override`}</span>
                        </McTooltipMDS>
                    )}

                    <BtnSep />

                    {/* Back */}
                    <McTooltipMDS onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                        {/* @ts-ignore */}
                        <McButton slot="trigger" fit="small" variant="plain" style={{ minWidth: 36, padding: "0 8px" }}
                            onClick={() => latestOnBack.current()}
                        >
                            <McIcon icon="arrow-left" />
                        </McButton>
                        <span>Go back</span>
                    </McTooltipMDS>
                    {/* Next */}
                    {onNext && (
                        <McTooltipMDS onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                            {/* @ts-ignore */}
                            <McButton slot="trigger" fit="small" variant="plain" appearance="primary"
                                onClick={() => latestOnNext.current?.()}
                            >
                                 <McIcon icon="arrow-right" />
                            </McButton>
                            <span>Next: Query Info</span>
                        </McTooltipMDS>
                    )}

                </div>
            </div>

            {/* ═══ SCHEMA PANEL OVERLAY (below toolbar) ═══ */}
            {schemaPanelOpen && onOpenSchemaPanelTab && (
                <>
                    <div
                        onClick={() => onToggleSchemaPanel?.()}
                        style={{
                            position: 'fixed',
                            top: schemaBackdropTop,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 1090,
                            background: 'rgba(15, 23, 42, 0.06)',
                        }}
                    />
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1100,
                        height: 'min(560px, calc(100vh - 180px))',
                        border: '1px solid #e5e7eb',
                        borderRadius: 0,
                        background: '#f8f9f6',
                        boxShadow: 'none',
                        overflow: 'hidden',
                        padding: '8px 12px 12px',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                            <McButton fit="small" variant="plain" onClick={() => onToggleSchemaPanel?.()}>
                                <McIcon icon="times" />
                            </McButton>
                        </div>
                        <SchemaExplorer
                            open={true}
                            onClose={() => onToggleSchemaPanel?.()}
                            terminalId={testTerminal}
                            databaseType={testDatabaseType}
                            renderMode="embedded"
                            defaultTab={schemaPanelTab}
                            onInsertSnippet={onInsertSnippet}
                        />
                    </div>
                </>
            )}

            {schemaExplorerWarning && (
                <div style={{
                    marginTop: 8,
                    marginBottom: 4,
                    padding: '8px 12px',
                    border: '1px solid #90caf9',
                    borderRadius: '8px',
                    background: '#e3f2fd',
                    color: '#0d47a1',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    <McIcon icon="info-circle" style={{ fontSize: '14px', flexShrink: 0 }} />
                    <span>{schemaExplorerWarning}</span>
                </div>
            )}
            </div>

            {/* ═══ MAIN AREA ═══ */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>

                {/* Editor column (vertical split: editor top, results bottom) */}
                <div ref={editorColumnRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

                    {/* Tip banner */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '3px 12px',
                        backgroundColor: '#fff8e1', borderBottom: '1px solid #ffe082',
                        fontSize: 11, color: '#141414', flexShrink: 0,
                    }}>
                        <McIcon icon="shield" style={{ fontSize: 12, color: '#f9a825', flexShrink: 0 }} />
                        <span>
                            <strong>Tip:</strong> Use <code>:paramName</code> for dynamic parameters. Limit with{' '}
                            {testDatabaseType === 'POSTGRESQL' ? <code>LIMIT 10</code> : <code>WHERE ROWNUM &lt;= 10</code>} for performance.
                        </span>
                    </div>

                    {/* SQL editor */}
                    <div style={{ flex: editorRatio, overflow: 'hidden', minHeight: 80 }}>
                        <SqlEditor
                            ref={sqlEditorRef}
                            value={sqlContent}
                            onChange={onSqlChange}
                            terminalId={testTerminal}
                            databaseType={testDatabaseType}
                            externalDdlEnabled={testDatabaseType === 'EXTERNAL' ? externalDdlEnabled : true}
                            externalConnectionServerType={externalConnectionServerType}
                            label="SQL Content"
                            required={true}
                            baseContent={overrideBaseSql}
                            isOverride={isOverride}
                        />
                    </div>

                    {/* Row-resize drag handle */}
                    <div
                        onMouseDown={handleResizerMouseDown}
                        style={{
                            height: 8, flexShrink: 0, cursor: 'row-resize',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: '#f9fafb',
                            borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb',
                        }}
                    >
                        <div style={{ width: 32, height: 2, backgroundColor: '#d1d5db', borderRadius: 1 }} />
                    </div>

                    {/* Results panel */}
                    <div style={{ flex: 1 - editorRatio, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 80 }}>
                        {/* Tab bar */}
                        <div style={{
                            display: 'flex', alignItems: 'center', height: 36, flexShrink: 0,
                            borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb', paddingLeft: 4,
                        }}>
                            {tabBtn('results', 'Results', !!testResults)}
                            {(onExplainPlan || isExplainingPlan || !!validationResult?.planLines?.length)
                                ? tabBtn('explain', 'Explain Plan', isExplainingPlan || !!validationResult?.planLines?.length)
                                : null}
                            {testResults?.data?.length
                                ? tabBtn('visualization', 'Visualization', false)
                                : null}
                            {testParameters.length > 0
                                ? tabBtn('parameters', `Parameters (${testParameters.length})`, false)
                                : null}
                            {/* Right side: row count + download */}
                            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, paddingRight: 12 }}>
                                {testResults?.data && (
                                    <span style={{ fontSize: 11, color: '#6b7280' }}>
                                        {filteredTestResults.length}
                                        {testResults.resultCount !== undefined ? `/${testResults.resultCount}` : ''} rows
                                    </span>
                                )}
                                {activeResultTab === 'results' && testResults && testResults.status !== 'ERROR' && (
                                    <DownloadResultsButton data={testResults.data} sqlQuery={sqlContent} />
                                )}
                            </div>
                        </div>

                        {/* Tab content */}
                        <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>

                            {/* RESULTS TAB */}
                            {activeResultTab === 'results' && (
                                <>
                                    {!testResults && !isExecuting && (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 16px', color: '#9ca3af', textAlign: 'center' }}>
                                            <McIcon icon="play-circle" style={{ fontSize: 32, marginBottom: 8 }} />
                                            <p className="mds-text--x-small-regular">Run the query to see results here.</p>
                                        </div>
                                    )}
                                    {isExecuting && (
                                        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                                            <McLoadingIndicator label="Executing query…" />
                                        </div>
                                    )}
                                    {aiCorrectionDiff && (
                                        <McNotification appearance="success" heading="SQL corrected by AI" style={{ marginBottom: 16 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <span>Review the changes before executing.</span>
                                                <McButton variant="filled" appearance="secondary" fit="small" onClick={onViewChanges}>
                                                    <McIcon icon="eye" slot="prefix" />View Changes
                                                </McButton>
                                                <McButton variant="outlined" fit="small" onClick={onUndoCorrection}>
                                                    <McIcon icon="arrow-anti-clockwise" slot="prefix" />Undo
                                                </McButton>
                                            </div>
                                        </McNotification>
                                    )}
                                    {validationResult && (
                                        <McNotification
                                            appearance={validationResult.isValid ? (validationResult.warnings?.length ? 'warning' : 'success') : 'error'}
                                            heading={validationResult.isValid ? (validationResult.warnings?.length ? 'SQL Valid with Warnings' : 'SQL is Valid') : 'SQL Validation Error'}
                                            style={{ marginBottom: 12 }}
                                        >
                                            {validationResult.validationMessage && (
                                                <div style={{ fontSize: '0.8rem', marginBottom: 4 }}>{validationResult.validationMessage}</div>
                                            )}
                                            {validationResult.warnings?.map((w: string, i: number) => (
                                                <div key={i} style={{ fontSize: '0.75rem' }}>• {w}</div>
                                            ))}
                                            {!validationResult.isValid && onAutoFix && (
                                                unfixableReason ? (
                                                    <div style={{ marginTop: 10, fontSize: '0.85em', color: '#6b6b6b' }}>
                                                        <McIcon icon="shield" style={{ fontSize: 14, marginRight: 4, verticalAlign: 'middle' }} />
                                                        {unfixableReason}
                                                    </div>
                                                ) : (
                                                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <McButton variant="filled" appearance="secondary" fit="small" onClick={onAutoFix} disabled={isAutoFixing}>
                                                            {isAutoFixing
                                                                ? <McLoadingIndicator slot="prefix" fit="small" />
                                                                : <McIcon icon="wrench" slot="prefix" />}
                                                            Try Auto Fix
                                                        </McButton>
                                                        <AutoFixModelPicker />
                                                    </div>
                                                )
                                            )}
                                        </McNotification>
                                    )}
                                    {testResults && testResults.status === 'ERROR' && (
                                        <McNotification appearance="error" heading="Execution Error" style={{ marginBottom: 16 }}>
                                            <div>{testResults.errorMessage || 'Query execution failed.'}</div>
                                            {onAutoFix && (
                                                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <McButton variant="filled" appearance="secondary" fit="small" onClick={onAutoFix} disabled={isAutoFixing}>
                                                        {isAutoFixing
                                                            ? <McLoadingIndicator slot="prefix" fit="small" />
                                                            : <McIcon icon="wrench" slot="prefix" />}
                                                        Try Auto Fix
                                                    </McButton>
                                                    <AutoFixModelPicker />
                                                </div>
                                            )}
                                        </McNotification>
                                    )}
                                    {testResults && testResults.status !== 'ERROR' && (
                                        <>
                                            {resultColumns.length > 0 && (
                                                <ResultFilterBar
                                                    columns={resultColumns.map((col: any) => ({
                                                        id: String(col?.id || ''),
                                                        label: String(col?.label || col?.id || ''),
                                                    }))}
                                                    filters={resultColumnFilters}
                                                    onFilterChange={setResultColumnFilters}
                                                />
                                            )}
                                            <McTable
                                                data={filteredTestResults}
                                                columns={resultColumns}
                                                fit="small"
                                                datakey={testResults.data.length > 0 ? Object.keys(testResults.data[0])[0] : 'id'}
                                            />
                                        </>
                                    )}
                                </>
                            )}

                            {/* EXPLAIN PLAN TAB */}
                            {activeResultTab === 'explain' && (
                                <>
                                    {!isExplainingPlan && !validationResult?.planLines?.length && !aiExplanation && (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 16px', color: '#9ca3af', textAlign: 'center' }}>
                                            <McIcon icon="list-bullets" style={{ fontSize: 32, marginBottom: 8 }} />
                                            <p className="mds-text--x-small-regular">Click &ldquo;Explain Plan&rdquo; to analyze the query execution path.</p>
                                        </div>
                                    )}
                                    <ExplainPlanCard
                                        planLines={validationResult?.planLines || []}
                                        isExplaining={isExplainingPlan}
                                        aiExplanation={aiExplanation}
                                        isGeneratingAiExplanation={isGeneratingAiExplanation}
                                        validationErrors={[
                                            ...(validationResult?.syntaxErrors || []),
                                            ...(validationResult?.vulnerabilities?.map((v: any) => v.description) || []),
                                        ]}
                                        onAiExplain={onAiExplain}
                                        onClose={onCloseExplainPlan}
                                        aiModelOptions={aiModelOptions}
                                        selectedModel={selectedModel}
                                        onModelChange={onModelChange}
                                    />
                                </>
                            )}
                            {/* VISUALIZATION TAB */}
                            {activeResultTab === 'visualization' && (
                                <ChartVisualization data={filteredTestResults?.length ? filteredTestResults : (testResults?.data || [])} />
                            )}
                            {/* PARAMETERS TAB */}
                            {activeResultTab === 'parameters' && (
                                <div>
                                    {testParameters.map((param, index) => (
                                        <ParameterRow
                                            key={param.name}
                                            name={param.name}
                                            type={param.type}
                                            testValue={param.value}
                                            description={param.description}
                                            onTypeChange={(v) => onTestParameterChange(index, 'type', v)}
                                            onTestValueChange={(v) => latestOnTestParameterChange.current(index, 'value', v)}
                                            onDescriptionChange={(v) => latestOnTestParameterChange.current(index, 'description', v)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>{/* /tab content */}
                    </div>{/* /results panel */}
                </div>{/* /editor column */}


            </div>{/* /main area */}
        </div>
    );
};
